import { Close, SaveOutlined } from '@mui/icons-material';
import {
  Alert,
  AppBar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  Divider,
  IconButton,
  MenuItem,
  Slide,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchData } from '../../helpers/apiUtils';
import { HubBroker, HubSite } from './types';

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface FactKeyDef {
  key: string;
  label: string;
  kind: 'text' | 'number' | 'url' | 'list';
  chip: boolean;
}

interface FactRow {
  broker_id: number;
  site_id: number;
  fact_key: string;
  value: string | null;
  updated_by: string | null;
}

interface OverviewEntry {
  broker_id: number;
  slug: string;
  name: string;
  master_keys: number;
  site_overrides: number;
  blocks_profiles: number;
}

/** What the rollout did with the saved values (mirrors StageResult in brokerFactsRollout.ts). */
interface RolloutReport {
  status: 'staged' | 'activated' | 'render_failed' | 'partial' | 'superseded';
  activated: boolean;
  message: string;
  targets: Array<{
    profile_id: number;
    site_key: string | null;
    page_slug: string | null;
    live: boolean;
    deployed: boolean | null;
    error?: string;
    warning?: string;
  }>;
}

/**
 * Turn the rollout report into what the operator needs to read.
 *
 * The important case is `partial`: the values ARE saved (so the API answers success) but at least
 * one live page still shows the old number. Reporting that as a plain "gespeichert" leaves the
 * one person who could fix it — the one looking at the screen — unaware that the hub and a portal
 * now disagree. The alert mail goes to somebody else, and the matrix only helps whoever looks.
 */
function rolloutMessage(
  savedCount: number,
  rollout?: RolloutReport,
): { severity: 'success' | 'warning' | 'error'; text: string } {
  if (!rollout) return { severity: 'success', text: `${savedCount} Feld(er) gespeichert.` };

  if (rollout.status === 'partial') {
    const broken = rollout.targets
      .filter((t) => t.deployed === false)
      .map((t) => `${t.site_key ?? '?'}/${t.page_slug ?? '?'}${t.error ? ` (${t.error})` : ''}`)
      .join(', ');
    return {
      severity: 'warning',
      text:
        `${savedCount} Feld(er) gespeichert — aber nicht überall ausgespielt. ` +
        `Diese Live-Seiten zeigen noch den alten Wert: ${broken}. ` +
        'Sie erscheinen in der Matrix als veraltet und müssen erneut ausgespielt werden.',
    };
  }

  const holed = rollout.targets.filter((t) => t.warning);
  if (holed.length) {
    return {
      severity: 'warning',
      text:
        `${savedCount} Feld(er) gespeichert. ${holed.length} Entwurfsseite(n) zitieren jetzt ` +
        `Werte, die es nicht mehr gibt (${holed.map((t) => t.page_slug ?? t.profile_id).join(', ')}) ` +
        '— die lassen sich erst nach einer Korrektur live schalten.',
    };
  }

  const pushed = rollout.targets.filter((t) => t.deployed === true).length;
  return {
    severity: 'success',
    text:
      pushed > 0
        ? `${savedCount} Feld(er) gespeichert und auf ${pushed} Live-Seite(n) ausgespielt.`
        : `${savedCount} Feld(er) gespeichert.`,
  };
}

interface Props {
  open: boolean;
  brokers: HubBroker[];
  sites: HubSite[];
  onClose: () => void;
}

/**
 * Broker facts (Increment 3) — the numbers a page may quote.
 *
 * One broker-level value with sparse per-portal overrides, mirroring the compliance store. The
 * blocks editor inserts them as `{{chips}}`; changing a value here changes it in every page that
 * quotes it, because the chip stores only the KEY and the deploy resolves the value.
 *
 * The portal scope deliberately shows an EMPTY field with the master value as a placeholder,
 * never the master's text as the field's value. Prefilling was the shape that produced the
 * compliance bug found in review: an innocent save turned into a portal override that shadowed a
 * perfectly good master. Empty here means "inherit", and that is what saving preserves.
 */
const FactsDialog: React.FC<Props> = ({ open, brokers, sites, onClose }) => {
  const [brokerId, setBrokerId] = useState<number | ''>('');
  const [siteId, setSiteId] = useState<number>(0);
  const [registry, setRegistry] = useState<FactKeyDef[]>([]);
  const [rows, setRows] = useState<FactRow[]>([]);
  const [overview, setOverview] = useState<OverviewEntry[]>([]);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showRegistry, setShowRegistry] = useState(false);
  const [message, setMessage] = useState<{
    severity: 'success' | 'warning' | 'error';
    text: string;
  } | null>(null);

  const loadRegistry = useCallback(async () => {
    const res = await fetchData<FactKeyDef[]>('/api/broker-hub/facts/registry');
    if (res.success && res.data) setRegistry(res.data);
  }, []);

  const loadOverview = useCallback(async () => {
    const res = await fetchData<OverviewEntry[]>('/api/broker-hub/facts/overview');
    if (res.success && res.data) setOverview(res.data);
  }, []);

  /** Same cancellation contract as ComplianceDialog: a late response for a broker the user has
   *  already left must not repaint the form under the new broker's id. */
  const loadRows = useCallback(
    async (id: number, isCurrent: () => boolean = () => true) => {
      setLoading(true);
      const res = await fetchData<FactRow[]>(`/api/broker-hub/facts/brokers/${id}`);
      if (!isCurrent()) return;
      setRows(res.success && res.data ? res.data : []);
      setLoading(false);
      loadOverview();
    },
    [loadOverview],
  );

  useEffect(() => {
    if (!open) return;
    loadRegistry();
    loadOverview();
    setMessage(null);
  }, [open, loadRegistry, loadOverview]);

  useEffect(() => {
    if (brokerId === '') return;
    // Drop the previous broker's rows immediately — the draft mirror below reacts to `rows`.
    setRows([]);
    setDraft({});
    let cancelled = false;
    loadRows(Number(brokerId), () => !cancelled);
    return () => {
      cancelled = true;
    };
  }, [brokerId, loadRows]);

  /** Value stored for the CURRENT scope only (not inherited) — that is what the field edits. */
  const ownValue = useCallback(
    (key: string): string => {
      const row = rows.find((r) => Number(r.site_id) === siteId && r.fact_key === key);
      return row?.value ?? '';
    },
    [rows, siteId],
  );

  /** The master value, shown as a placeholder on a portal scope. */
  const masterValue = useCallback(
    (key: string): string =>
      rows.find((r) => Number(r.site_id) === 0 && r.fact_key === key)?.value ?? '',
    [rows],
  );

  // Reset the draft whenever the underlying rows or the scope change.
  useEffect(() => {
    setDraft({});
  }, [rows, siteId]);

  const fieldValue = (key: string): string => draft[key] ?? ownValue(key);
  const dirtyKeys = useMemo(
    () => Object.keys(draft).filter((k) => (draft[k] ?? '') !== ownValue(k)),
    [draft, ownValue],
  );

  const save = async () => {
    if (brokerId === '' || dirtyKeys.length === 0) return;
    setBusy(true);
    setMessage(null);
    const res = (await fetchData<FactRow[]>(`/api/broker-hub/facts/brokers/${brokerId}`, {
      method: 'PUT',
      body: JSON.stringify({
        // Only what actually changed — a blanket write would touch every key's updated_by and
        // make "who last changed this number" useless.
        facts: dirtyKeys.map((key) => ({ site_id: siteId, fact_key: key, value: draft[key] })),
      }),
      // fetchData returns the response body verbatim on success, so the rollout report comes
      // through — it is just not part of the helper's declared type.
    })) as { success: boolean; data?: FactRow[]; error?: string; rollout?: RolloutReport };
    if (res.success && res.data) {
      setRows(res.data);
      setDraft({});
      loadOverview();
      setMessage(rolloutMessage(dirtyKeys.length, res.rollout));
    } else {
      setMessage({ severity: 'error', text: res.error || 'Speichern fehlgeschlagen' });
    }
    setBusy(false);
  };

  const updateRegistryAt = (i: number, patch: Partial<FactKeyDef>) =>
    setRegistry((r) => r.map((d, j) => (j === i ? { ...d, ...patch } : d)));

  const saveRegistry = async () => {
    setBusy(true);
    setMessage(null);
    const res = await fetchData<FactKeyDef[]>('/api/broker-hub/facts/registry', {
      method: 'PUT',
      body: JSON.stringify({ keys: registry }),
    });
    if (res.success && res.data) {
      // Take the SERVER's version back, not the local draft: it normalises labels and forces
      // chip=false for list/url kinds, so keeping the draft would show a state the store rejects.
      setRegistry(res.data);
      setMessage({ severity: 'success', text: 'Feldliste gespeichert.' });
    } else {
      setMessage({ severity: 'error', text: res.error || 'Speichern fehlgeschlagen' });
    }
    setBusy(false);
  };

  const selectedBroker = brokers.find((b) => b.id === brokerId);
  const coverage = overview.find((o) => o.broker_id === brokerId);
  const unmaintained = overview.filter((o) => o.blocks_profiles > 0 && o.master_keys === 0);

  return (
    <Dialog fullScreen open={open} onClose={onClose} TransitionComponent={Transition}>
      <AppBar sx={{ position: 'relative' }}>
        <Toolbar>
          <Typography sx={{ flex: 1 }} variant="h6">
            Broker-Fakten — zentrale Werte für Texte und Widgets
          </Typography>
          <IconButton edge="end" color="inherit" onClick={onClose} aria-label="Schließen">
            <Close />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box p={3} display="flex" flexDirection="column" gap={2} maxWidth={1100}>
        {/* ⚠ Dieser Text ist der Grund, warum jemand getippte Klammern probiert. Bis 2026-09-21
            stand hier „Diese Werte lassen sich im Blocks-Editor mit ‚{' direkt in den Text
            setzen" — pauschal, obwohl es nur für Felder mit Chip gilt. Die CFD-Felder haben
            keinen, sie erreichen eine Seite ausschliesslich über die Broker-Zeilen der
            Vergleichsseiten. Wer das nicht weiss, tippt {{broker.spread}} und wundert sich.
            Die Fassung unten nennt alle drei Wege einzeln. */}
        <Alert severity="info">
          Ein Wert erreicht eine Seite auf drei Wegen, und welcher gilt, steht unter jedem Feld:
          <br />
          <strong>im Text</strong> — nur Felder mit Chip, im Editor über „&#123;“ eingefügt.
          Getippte geschweifte Klammern funktionieren <strong>nicht</strong> und werden beim
          Veröffentlichen abgewiesen.
          <br />
          <strong>am Widget</strong> — Affiliate-Knopf, Notenzeile und Broker-Kopf lassen sich in
          den Bausteineinstellungen an ein Feld binden.
          <br />
          <strong>über die Broker-Zeilen</strong> — Note und CFD-Felder speisen Vergleichsseiten
          direkt; dort muss nichts eingefügt werden.
          <br />
          Gespeichert wird immer nur der <strong>Name</strong> des Feldes, der Wert kommt beim
          Veröffentlichen dazu. Eine Änderung hier spielt betroffene Live-Seiten neu aus; fehlt ein
          zitierter Wert, lehnt der Hub den Live-Deploy ab.
        </Alert>

        {message && (
          <Alert severity={message.severity} onClose={() => setMessage(null)}>
            {message.text}
          </Alert>
        )}

        {unmaintained.length > 0 && (
          <Alert severity="warning">
            <strong>
              {unmaintained.length} Broker mit Blocks-Profil, aber ohne gepflegte Fakten:
            </strong>
            <Box display="flex" gap={0.5} flexWrap="wrap" mt={1}>
              {unmaintained.map((m) => (
                <Chip
                  key={m.broker_id}
                  size="small"
                  label={m.name}
                  onClick={() => {
                    setBrokerId(m.broker_id);
                    setSiteId(0);
                  }}
                />
              ))}
            </Box>
          </Alert>
        )}

        <Box display="flex" gap={2} flexWrap="wrap">
          <TextField
            select
            label="Broker"
            size="small"
            sx={{ minWidth: 240 }}
            value={brokerId}
            onChange={(e) => setBrokerId(e.target.value === '' ? '' : Number(e.target.value))}
          >
            <MenuItem value="">— auswählen —</MenuItem>
            {brokers.map((b) => (
              <MenuItem key={b.id} value={b.id}>
                {b.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Geltungsbereich"
            size="small"
            sx={{ minWidth: 240 }}
            value={siteId}
            onChange={(e) => setSiteId(Number(e.target.value))}
          >
            <MenuItem value={0}>Alle Portale (Master)</MenuItem>
            {sites.map((s) => (
              <MenuItem key={s.id} value={s.id}>
                Nur {s.name}
              </MenuItem>
            ))}
          </TextField>
          {coverage && (
            <Box display="flex" alignItems="center" gap={1}>
              <Chip size="small" label={`${coverage.master_keys} Master-Werte`} />
              {coverage.site_overrides > 0 && (
                <Chip
                  size="small"
                  color="info"
                  label={`${coverage.site_overrides} Portal-Abweichungen`}
                />
              )}
              {coverage.blocks_profiles > 0 && (
                <Chip
                  size="small"
                  color="warning"
                  label={`${coverage.blocks_profiles} Blocks-Seite(n) betroffen`}
                />
              )}
            </Box>
          )}
        </Box>

        {siteId !== 0 && (
          <Alert severity="info">
            Portal-Ansicht: Ein <strong>leeres</strong> Feld bedeutet „gilt der Master“. Nur was du
            hier einträgst, weicht für {sites.find((s) => s.id === siteId)?.name} ab. Ein bereits
            abweichendes Feld leerst du, um wieder den Master gelten zu lassen.
          </Alert>
        )}

        <Divider />

        {brokerId === '' ? (
          <Typography color="text.secondary">Bitte oben einen Broker auswählen.</Typography>
        ) : loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" gap={1.5}>
            {registry.map((def) => {
              const master = masterValue(def.key);
              const isList = def.kind === 'list';
              return (
                <TextField
                  key={def.key}
                  label={def.label}
                  size="small"
                  fullWidth
                  multiline={isList}
                  minRows={isList ? 2 : undefined}
                  value={fieldValue(def.key)}
                  placeholder={
                    siteId !== 0 && master
                      ? `Master: ${master}`
                      : isList
                        ? 'Eine Zeile pro Eintrag'
                        : ''
                  }
                  onChange={(e) => setDraft((d) => ({ ...d, [def.key]: e.target.value }))}
                  helperText={[
                    `Feldname: ${def.key}`,
                    // ⚠ „nur für Widgets" stand hier bis 2026-09-21 für JEDES Nicht-Chip-Feld
                    // und war für die CFD-Felder schlicht falsch: bindbar sind nur
                    // affButton.url, ratingSmall.value und brokerHero.url
                    // (brokerFacts.ts § WIDGET_FACT_BINDINGS). Ein Spread landet dort nie —
                    // er speist die Broker-Zeilen der Vergleichsseiten
                    // (brokerRoster.ts § ROSTER_FAKT_SCHLUESSEL).
                    def.chip
                      ? 'im Text einsetzbar'
                      : def.key.startsWith('cfd_')
                        ? 'speist die Broker-Zeilen der Vergleichsseiten'
                        : 'nur als Widget-Bindung',
                    isList ? 'eine Zeile pro Eintrag' : null,
                    siteId !== 0 && !master ? 'kein Master-Wert hinterlegt' : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                />
              );
            })}
            {registry.length === 0 && (
              <Alert severity="warning">
                Es sind keine Faktenfelder definiert. Läuft die Migration 20260803c auf dieser
                Datenbank?
              </Alert>
            )}
          </Box>
        )}

        <Box display="flex" gap={1} alignItems="center">
          <Button
            variant="contained"
            startIcon={busy ? <CircularProgress size={16} /> : <SaveOutlined />}
            disabled={busy || loading || brokerId === '' || dirtyKeys.length === 0}
            onClick={save}
          >
            Speichern
          </Button>
          <Button size="small" onClick={() => setShowRegistry((v) => !v)}>
            {showRegistry ? 'Feldliste schließen' : 'Feldliste bearbeiten'}
          </Button>
          <Typography variant="caption" color="text.secondary">
            {dirtyKeys.length > 0
              ? `${dirtyKeys.length} geändert: ${dirtyKeys.join(', ')}`
              : 'Keine Änderungen'}
            {selectedBroker ? ` · ${selectedBroker.name}` : ''}
          </Typography>
        </Box>

        {showRegistry && (
          <>
            <Divider />
            <Typography variant="subtitle2">Feldliste</Typography>
            <Alert severity="warning">
              Ein Feld zu <strong>entfernen</strong> löscht seine Werte nicht, macht sie aber
              unauflösbar — Seiten, die es zitieren, lassen sich dann nicht mehr live schalten, bis
              der Platzhalter aus dem Text verschwindet. Umbenennen ist dasselbe in Grün: der
              Schlüssel steht in jedem Text, der ihn zitiert.
            </Alert>
            {registry.map((def, i) => (
              <Box key={def.key} display="flex" gap={1} alignItems="center" flexWrap="wrap">
                <TextField
                  size="small"
                  label="Schlüssel"
                  sx={{ width: 200 }}
                  value={def.key}
                  onChange={(e) => updateRegistryAt(i, { key: e.target.value })}
                />
                <TextField
                  size="small"
                  label="Beschriftung"
                  sx={{ width: 240 }}
                  value={def.label}
                  onChange={(e) => updateRegistryAt(i, { label: e.target.value })}
                />
                <TextField
                  select
                  size="small"
                  label="Art"
                  sx={{ width: 150 }}
                  value={def.kind}
                  onChange={(e) =>
                    updateRegistryAt(i, { kind: e.target.value as FactKeyDef['kind'] })
                  }
                >
                  <MenuItem value="text">Text</MenuItem>
                  <MenuItem value="number">Zahl</MenuItem>
                  <MenuItem value="url">Link</MenuItem>
                  <MenuItem value="list">Liste</MenuItem>
                </TextField>
                <Typography variant="caption" color="text.secondary" sx={{ width: 120 }}>
                  {def.kind === 'list' || def.kind === 'url' ? 'nur Widgets' : 'im Text einsetzbar'}
                </Typography>
                <Button
                  size="small"
                  color="error"
                  onClick={() => setRegistry((r) => r.filter((_, j) => j !== i))}
                >
                  Entfernen
                </Button>
              </Box>
            ))}
            <Box display="flex" gap={1}>
              <Button
                size="small"
                onClick={() =>
                  setRegistry((r) => [
                    ...r,
                    { key: '', label: '', kind: 'text' as const, chip: true },
                  ])
                }
              >
                Feld hinzufügen
              </Button>
              <Button size="small" variant="contained" disabled={busy} onClick={saveRegistry}>
                Feldliste speichern
              </Button>
              <Button size="small" onClick={loadRegistry} disabled={busy}>
                Verwerfen
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Dialog>
  );
};

export default FactsDialog;
